import { useState, useRef } from "react";
import { apiRequest, useDatadog } from "@vojomo-core/shared";
import { message } from "antd";

interface UploadedFile {
  gcs_path: string;
  inserted_id: string;
}

interface UseFileUploadOptions {
  onUploadSuccess?: (data: UploadedFile) => void;
  onUploadError?: (error: any) => void;
}

export const useFileUpload = (options?: UseFileUploadOptions) => {
  const { trackEvent, trackError } = useDatadog();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [newFileName, setNewFileName] = useState("");

  // Modal states
  const [sameNameModalOpen, setSameNameModalOpen] = useState(false);
  const [sameHashModalOpen, setSameHashModalOpen] = useState(false);
  const [sameHashModalFiles, setSameHashModalFiles] = useState<any[]>([]);
  const [isNewFileNameTaken, setIsNewFileNameTaken] = useState(false);
  const [ignoreSameHash, setIgnoreSameHash] = useState(false);

  // Helper to convert file to base64
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  // Helper function to get file extension
  const getFileExtension = (filename: string): string => {
    const parts = filename.split(".");
    return parts.length > 1 ? `.${parts[parts.length - 1]}` : "";
  };

  // Helper function to ensure filename has the correct extension
  const ensureFileExtension = (
    newName: string,
    originalName: string
  ): string => {
    const originalExt = getFileExtension(originalName);
    if (newName.endsWith(originalExt)) {
      return newName;
    }

    const newNameWithoutExt = newName.includes(".")
      ? newName.substring(0, newName.lastIndexOf("."))
      : newName;

    return `${newNameWithoutExt}${originalExt}`;
  };

  // Check if a filename already exists
  const checkFileNameExists = async (fileName: string): Promise<boolean> => {
    try {
      const check_file_name = await apiRequest<any>({
        subRoute: "ai",
        path: `/check_file_name?file_name=${encodeURIComponent(fileName)}`,
        method: "GET"
      });
      return check_file_name.length > 0;
    } catch (error) {
      trackError("error_checking_file_name", error, { fileName: fileName });
      return false;
    }
  };

  // Upload file to server
  const uploadFile = async (file: File): Promise<UploadedFile> => {
    const base64_file = await getBase64(file);

    trackEvent("uploading_file", {
      fileType: file.type,
      fileSize: file.size,
      fileName: file.name
    });

    const data = await apiRequest<UploadedFile>({
      subRoute: "ai",
      path: `/upload_file`,
      method: "POST",
      body: {
        base64_file: base64_file,
        file_type: file.type,
        file_name: file.name
      }
    });

    return data;
  };

  // Handle rename and upload
  const handleRenameAndUpload = async () => {
    if (!currentFile || !newFileName) {
      message.error("File or new name is missing");
      return;
    }

    const correctedFileName = ensureFileExtension(
      newFileName,
      currentFile.name
    );
    setUploading(true);

    try {
      const fileNameExists = await checkFileNameExists(correctedFileName);

      if (fileNameExists) {
        trackError("file_name_already_exists", { fileName: correctedFileName });
        setIsNewFileNameTaken(true);
        message.error(
          `The name "${correctedFileName}" is already taken. Please choose another name.`
        );
        setUploading(false);
        return;
      }

      const renamedFile = new File([currentFile], correctedFileName, {
        type: currentFile.type
      });

      const data = await uploadFile(renamedFile);

      message.success("File uploaded successfully!");
      setSameNameModalOpen(false);
      setNewFileName("");
      setIsNewFileNameTaken(false);
      setCurrentFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      options?.onUploadSuccess?.(data);
    } catch (error) {
      trackError("error_uploading_renamed_file", error);
      message.error("Error uploading renamed file");
      options?.onUploadError?.(error);
    } finally {
      setUploading(false);
    }
  };

  // Process file upload with hash and name checks
  const processFileUpload = async (
    fileToProcess: File,
    skipHashCheck: boolean = false
  ): Promise<boolean> => {
    // Validate file size
    if (fileToProcess.size > 10 * 1024 * 1024) {
      trackError("file_too_large_upload", {
        fileSize: fileToProcess.size,
        fileType: fileToProcess.type
      });
      message.error("File is too large. Maximum size is 10MB.");
      return false;
    }

    // Validate file type
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain"
    ];

    if (!validTypes.includes(fileToProcess.type)) {
      trackError("invalid_file_type_upload", {
        fileSize: fileToProcess.size,
        fileType: fileToProcess.type
      });

      message.error(
        "Invalid file type. Only PDF, DOC, DOCX, and TXT files are supported."
      );
      return false;
    }

    try {
      const base64_file = await getBase64(fileToProcess);
      setUploading(true);

      // Check file hash if not skipping
      if (!skipHashCheck) {
        const check_file_hash = await apiRequest<any>({
          subRoute: "ai",
          path: `/check_file_hash`,
          method: "POST",
          body: {
            base64_file: base64_file
          }
        });

        if (check_file_hash.length > 0) {
          trackEvent("same_hash_file_upload", {
            hash: check_file_hash,
            fileName: fileToProcess.name,
            fileSize: fileToProcess.size,
            fileType: fileToProcess.type
          });
          setSameHashModalFiles(check_file_hash);
          setSameHashModalOpen(true);
          setUploading(false);
          return false;
        }
      }

      // Check if file name already exists
      const check_file_name = await apiRequest<any>({
        subRoute: "ai",
        path: `/check_file_name?file_name=${encodeURIComponent(fileToProcess.name)}`,
        method: "GET"
      });

      if (check_file_name.length > 0) {
        trackEvent("same_name_file_upload", {
          fileName: fileToProcess.name,
          fileType: fileToProcess.type,
          fileSize: fileToProcess.size
        });
        setSameNameModalOpen(true);
        setNewFileName(fileToProcess.name);
        setUploading(false);
        return false;
      }

      // Upload the file
      const data = await uploadFile(fileToProcess);

      message.success("File uploaded successfully!");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      options?.onUploadSuccess?.(data);
      return true;
    } catch (error) {
      trackError("error_file_upload", error, {
        fileName: fileToProcess.name,
        fileSize: fileToProcess.size,
        fileType: fileToProcess.type
      });
      message.error("Failed to upload file. Please try again.");
      options?.onUploadError?.(error);
      return false;
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCurrentFile(file);

    try {
      const success = await processFileUpload(file, ignoreSameHash);
      // Only clear currentFile if upload was successful
      if (success) {
        setCurrentFile(null);
      }
    } finally {
      setIgnoreSameHash(false);
    }
  };

  // Continue upload after hash warning
  const continueUploadAfterHashWarning = async () => {
    if (!currentFile) return;

    setSameHashModalOpen(false);

    try {
      const success = await processFileUpload(currentFile, true);
      // Only clear currentFile if upload was successful
      if (success) {
        setCurrentFile(null);
      }
    } finally {
      setIgnoreSameHash(false);
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Reset state
  const resetUploadState = () => {
    setSameNameModalOpen(false);
    setSameHashModalOpen(false);
    setCurrentFile(null);
    setNewFileName("");
    setIsNewFileNameTaken(false);
    setIgnoreSameHash(false);
    setSameHashModalFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return {
    // Refs
    fileInputRef,

    // State
    uploading,
    currentFile,
    newFileName,
    setNewFileName,
    sameNameModalOpen,
    setSameNameModalOpen,
    sameHashModalOpen,
    setSameHashModalOpen,
    sameHashModalFiles,
    isNewFileNameTaken,
    setIsNewFileNameTaken,

    // Methods
    handleFileSelect,
    handleRenameAndUpload,
    continueUploadAfterHashWarning,
    triggerFileInput,
    resetUploadState,
    checkFileNameExists,
    ensureFileExtension
  };
};
